import { useEffect, useState } from 'react'
import { Loader2, Plane, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type { OperatorDetail, WatchRuleInput } from '@/types/airTraffic'
import { AIR, CLASS_COLOR, FONT_MONO, classify, fmtAlt } from '../theme'
import { ActionButton, Empty, KV, SectionLabel, downloadReport, errorText } from '../ui'

interface Props {
  name: string
  watched: Set<string>
  onClose: () => void
  onOpenAircraft: (hex: string, lon?: number, lat?: number) => void
  onWatch: (rule: WatchRuleInput) => Promise<void>
}

export default function OperatorInspector({ name, watched, onClose, onOpenAircraft, onWatch }: Props) {
  const [d, setD] = useState<OperatorDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setD(null)
    setError(null)
    apiClient
      .airOperator(name)
      .then((r) => !cancelled && setD(r))
      .catch((e) => !cancelled && setError(errorText(e)))
    return () => {
      cancelled = true
    }
  }, [name])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: AIR.muted }}>
            Operator
          </div>
          <div className="truncate text-[16px] font-semibold leading-tight" style={{ color: AIR.bright }}>
            {name}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-sm p-1 hover:bg-white/5">
          <X className="h-4 w-4" style={{ color: AIR.muted }} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {error && <Empty>{error}</Empty>}
        {!d && !error && (
          <div className="flex items-center gap-2 px-3 py-4 text-[12px]" style={{ color: AIR.muted }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}
        {d && (
          <>
            <SectionLabel>Now</SectionLabel>
            <KV k="Airborne" v={d.total} />
            <KV k="Military" v={d.composition.military} />
            <KV k="Types" v={Object.entries(d.composition.by_type).slice(0, 6).map(([k, v]) => `${k} ${v}`).join(', ')} />
            <SectionLabel>Last 30 days</SectionLabel>
            {d.history.sightings ? (
              <>
                <KV k="Sightings" v={d.history.sightings} />
                <KV k="Airframes" v={d.history.airframes} />
                <KV k="Types" v={Object.keys(d.history.types || {}).slice(0, 6).join(', ')} />
                <KV k="Busiest day" v={Object.entries(d.history.busiest_days || {})[0]?.join(' · ')} />
              </>
            ) : (
              <Empty>History builds up from today for this operator’s military, privacy-listed and watched aircraft.</Empty>
            )}
            <SectionLabel>Airborne aircraft</SectionLabel>
            {d.airborne.length === 0 && <Empty>None of this operator’s aircraft are being received right now.</Empty>}
            {d.airborne.slice(0, 60).map((a) => (
              <button
                key={a.hex}
                type="button"
                onClick={() => onOpenAircraft(a.hex, a.lon, a.lat)}
                className="flex w-full items-center gap-2 px-3 py-1 text-left text-[12px] hover:bg-white/[0.03]"
              >
                <Plane className="h-3 w-3 shrink-0" style={{ color: CLASS_COLOR[classify(a, watched)], transform: `rotate(${(a.trk ?? 45) - 45}deg)` }} />
                <span className="w-[76px] shrink-0 truncate" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
                  {a.cs || a.reg || a.hex.toUpperCase()}
                </span>
                <span className="w-10 shrink-0" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                  {a.type || '—'}
                </span>
                <span className="min-w-0 flex-1 truncate text-right" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                  {fmtAlt(a)}
                </span>
              </button>
            ))}
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 border-t px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <ActionButton tone="primary" onClick={() => downloadReport('operator', { name }, name)}>
          Report
        </ActionButton>
        <ActionButton onClick={() => onWatch({ kind: 'operator', value: name })}>Watch operator</ActionButton>
      </div>
    </div>
  )
}
